import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { name: "", location: "", avatar: "", rating: 5, text: "", tour: "" };
const input = "w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-brandorange";

export default function AdminTestimonials() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/testimonials").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.text) { toast.error("Name and text required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, rating: Number(form.rating) };
      if (editId) await api.put(`/admin/testimonials/${editId}`, payload);
      else await api.post("/admin/testimonials", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/testimonials/${id}`); load(); };

  return (
    <AdminLayout title="Testimonials">
      <div className="mb-6 flex justify-end"><button onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }} data-testid="add-testimonial-btn" className="btn-gradient flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold"><Plus className="h-4 w-4" /> New Testimonial</button></div>
      {items === null ? <div className="grid gap-4 sm:grid-cols-2">{Array.from({ length: 2 }).map((_, i) => <div key={i} className="h-36 skeleton rounded-2xl" />)}</div> : (
        <div className="grid gap-4 sm:grid-cols-2">
          {items.map((t) => (
            <div key={t.id} className="rounded-2xl border border-gray-100 bg-white p-5 card-soft">
              <div className="flex justify-between"><div className="flex gap-1">{Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="h-4 w-4 fill-brandorange text-brandorange" />)}</div>
                <div className="flex gap-1"><button onClick={() => { setForm({ ...EMPTY, ...t }); setEditId(t.id); setOpen(true); }} className="rounded-lg p-1.5 text-blue-600 hover:bg-blue-50"><Pencil className="h-4 w-4" /></button><button onClick={() => del(t.id)} className="rounded-lg p-1.5 text-brandred hover:bg-red-50"><Trash2 className="h-4 w-4" /></button></div>
              </div>
              <p className="mt-2 text-sm text-gray-600">"{t.text}"</p>
              <p className="mt-3 text-sm font-bold text-navy">{t.name} <span className="font-normal text-gray-400">· {t.location}</span></p>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle className="font-display text-2xl text-navy">{editId ? "Edit" : "New"} Testimonial</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <input data-testid="testimonial-name" className={input} placeholder="Name" value={form.name} onChange={set("name")} />
            <div className="grid grid-cols-2 gap-3"><input className={input} placeholder="Location" value={form.location} onChange={set("location")} /><input className={input} placeholder="Tour" value={form.tour} onChange={set("tour")} /></div>
            <select className={input} value={form.rating} onChange={set("rating")}>{[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} Stars</option>)}</select>
            <textarea rows={3} data-testid="testimonial-text" className={input} placeholder="Testimonial text" value={form.text} onChange={set("text")} />
            <button data-testid="save-testimonial-btn" disabled={saving} className="btn-gradient flex w-full items-center justify-center rounded-full py-3 font-bold disabled:opacity-70">{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save"}</button>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
